import { Card } from "@/components/Card";
import { Button } from "@/components/generic/Button";
import { Checkbox } from "@/components/generic/Checkbox";
import { Select, SelectItem } from "@/components/generic/Select";
import { TextField } from "@/components/generic/TextField";
import { useIntegrationSetup } from "@/components/integrations/IntegrationSetupContext";
import {
  useConnectIntegrationMutation,
  useIntegrationSettingsQuery,
} from "@/queries/integrations";
import { ProviderConnectionInfo } from "@/types/integrations";

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../campaign/basic/SegmentHeader";

const settingsFormSchema = z.record(
  z.string(),
  z.union([z.string(), z.boolean(), z.number()]),
);

export interface SettingsStepProps {
  /** Called when the step completes and should proceed to next */
  onNext?: (updatedConnectionInfo?: ProviderConnectionInfo) => void;
  /** Called when user goes back */
  onBack?: () => void;
}

export function SettingsStep({ onNext, onBack }: SettingsStepProps) {
  const {
    integration,
    mode,
    connectionInfo,
    setConnectionInfo,
    getSessionKey,
  } = useIntegrationSetup();

  const { data: settings = [] } = useIntegrationSettingsQuery(integration);

  const sessionDefaults = JSON.parse(
    sessionStorage.getItem(getSessionKey("settings")) ?? "{}",
  );

  const methods = useForm({
    mode: "onChange",
    resolver: zodResolver(settingsFormSchema),
    defaultValues: sessionDefaults,
  });

  const { mutateAsync } = useConnectIntegrationMutation(
    mode === "setup" ? "post" : "put",
  );

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
    watch,
  } = methods;

  const watchedForm = watch();

  const onSubmit = async (data: z.output<typeof settingsFormSchema>) => {
    const otherSettings: Record<string, string> = {};
    for (const [key, value] of Object.entries(data)) {
      otherSettings[key] = String(value);
    }

    const updatedConnectionInfo: ProviderConnectionInfo | null = connectionInfo
      ? {
          ...connectionInfo,
          settings: connectionInfo.settings
            ? { ...connectionInfo.settings, other: otherSettings }
            : { exportMappings: [], other: otherSettings },
        }
      : null;

    if (updatedConnectionInfo) {
      setConnectionInfo(updatedConnectionInfo);
    }

    // Store form data in session storage
    sessionStorage.setItem(getSessionKey("settings"), JSON.stringify(data));

    // Navigate to next step
    onNext?.(updatedConnectionInfo ?? undefined);
  };

  const handleBack = () => {
    onBack?.();
  };

  // Seed form defaults from each field's defaultValue when no session data exists
  useEffect(() => {
    if (!settings?.length) return;
    if (Object.keys(sessionDefaults).length > 0) return;

    // If we're updating, we should seed the form with existing connection info instead of field defaults
    if (mode === "update") {
      if (!connectionInfo) return;

      const otherSettings = connectionInfo.settings?.other ?? {};
      reset(otherSettings);
      return;
    }

    const fieldDefaults: Record<string, string> = {};
    for (const section of settings) {
      for (const field of section.fields) {
        if (field.defaultValue != null) {
          fieldDefaults[field.id] = field.defaultValue;
        }
      }
    }
    if (Object.keys(fieldDefaults).length > 0) {
      reset(fieldDefaults);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, connectionInfo]);

  // Render the form sections and their fields
  const renderSections = () =>
    settings.map((section) => (
      <div key={section.title} className="flex flex-col gap-y-4">
        <SegmentHeader
          title={section.title}
          description={section.description}
        />

        <div className="flex flex-col gap-y-4">
          {section.fields.map((field) => {
            switch (field.type) {
              case "Text":
                return (
                  <TextField
                    key={field.id}
                    label={field.name}
                    description={field.description}
                    name={field.id}
                    control={control}
                    isRequired={field.mandatory}
                  />
                );
              case "Dropdown":
                return (
                  <Controller
                    key={field.id}
                    name={field.id}
                    control={control}
                    render={({ field: rhfField }) => (
                      <Select
                        label={field.name}
                        aria-label={field.name}
                        isRequired={field.mandatory}
                        selectedKey={rhfField.value as string | null}
                        onSelectionChange={rhfField.onChange}
                        items={field.options.map((o) => ({
                          id: o.id,
                          label: o.value,
                        }))}
                      >
                        {(item) => (
                          <SelectItem id={item.id}>{item.label}</SelectItem>
                        )}
                      </Select>
                    )}
                  />
                );
              case "Checkbox":
                return (
                  <Card
                    theme="border"
                    className="flex rounded-md p-8"
                    key={field.id}
                  >
                    <Checkbox
                      size="md"
                      className="gap-6"
                      isRequired={field.mandatory}
                    >
                      <div className="flex flex-col gap-y-1">
                        <span className="text-sm font-medium">
                          {field.name}
                        </span>
                        <span className="text-theme-on-background-variant text-xs">
                          {field.description}
                        </span>
                      </div>
                    </Checkbox>
                  </Card>
                );
              default:
                return (
                  <TextField
                    key={field.id}
                    label={field.name}
                    description={field.description}
                    name={field.id}
                    control={control}
                    isRequired={field.mandatory}
                  />
                );
            }
          })}
        </div>
      </div>
    ));

  return (
    <div className="mx-auto h-full">
      {/* Form */}
      <form
        id="setupForm"
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col gap-y-14"
      >
        <div className="flex flex-col gap-y-8">{renderSections()}</div>

        {import.meta.env.DEV && (
          <details>
            <summary>debug form</summary>
            <pre>{JSON.stringify(watchedForm, undefined, 2)}</pre>
          </details>
        )}

        {/* Navigation buttons */}
        <div className="mt-auto flex justify-center gap-x-4">
          {mode === "setup" && onBack && (
            <Button type="button" intent="tertiary" onClick={handleBack}>
              Tilbage
            </Button>
          )}

          <Button
            type="submit"
            intent={mode === "setup" ? "secondary" : "primary"}
            isDisabled={!isValid}
          >
            {mode === "setup" ? "Næste" : "Gem ændringer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
